/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class QueryAPI
 * @hideconstructor
 * @classdesc Query API class that is used to construct and run a query.
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./QueryDataItem',
	'./api/QueryAPI',
	'../../../filters/FilterQuerySpec',
	'./QuerySelectionBuilder',
	'../../content/dataQueryExecution/DataQueryUtils',
	'../../../lib/@waca/dashboard-common/dist/core/UniqueHashIdBuilder'
], (_, APIFactory, QueryDataItem, QueryAPI, FilterQuerySpec, QuerySelectionBuilder, DataQueryUtils, UniqueHashIdBuilder) => {

	class Query {

		VERSION = '1';
		DEFAULT_ROW_LIMIT = 1000;
		DEFAULT_OFFSET = 0;

		constructor(dashboard, queryService, type) {
			this._dashboard = dashboard;
			this._queryService = queryService;
			this._logger = dashboard.getGlassCoreSvc('.Logger');
			this._type = type;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryAPI]);
			}
			return this.api;
		}

		setDataSource(sourceId) {
			const dataSources = this._dashboard.getFeature('DataSources');
			this._dataSource = dataSources.getDataSource(sourceId);
			return this._dataSource.loadMetadata();
		}

		addColumns(columnIdList) {
			if (!this._dataItems) {
				this._dataItems = [];
				this.dataItemIdsMap = {};
			}

			columnIdList.forEach(columnId => {
				const metadataColumn = this._dataSource.getMetadataColumn(columnId);
				if (metadataColumn) {
					this._dataItems.push(this._createQueryDataItem(metadataColumn, columnId));
				} else {
					this._logger.error('Could not find metadata column ' + columnId + ' in ' + this._dataSource.getId());
				}
			});

			return this._dataItems;
		}

		addDataItems(dataItemList) {
			if (!this._dataItems) {
				this._dataItems = [];
				this.dataItemIdsMap = {};
			}

			if (dataItemList) {
				dataItemList.forEach(dataItem => {
					const metadataColumn = dataItem.getMetadataColumn();
					this._dataItems.push(this._createQueryDataItem(metadataColumn, null, dataItem));
				});
			}

			return this._dataItems;
		}

		_createQueryDataItem(metadataColumn, columnId, dataItem) {
			const idOrigin = dataItem && dataItem.getId() || columnId;
			const dataItemId = UniqueHashIdBuilder.createUniqueHashId(idOrigin, this.dataItemIdsMap);
			this.dataItemIdsMap[dataItemId] = dataItemId;
			return new QueryDataItem(metadataColumn, dataItemId, dataItem).getAPI();
		}

		addFilters(filters) {
			if (!this._filters) {
				this._filters = [];
			}
			if (filters && filters.length > 0) {
				this._filters.push(...filters);
			}
		}

		addSearchTerms(columnId, searchTermList = []) {
			if (!this._searchTerms) {
				this._searchTerms = [];
			}

			if(!Array.isArray(searchTermList) || !searchTermList.length > 0) {
				return;
			}

			const filter = {
				id: columnId,
				columnId: columnId,
				values: searchTermList
			};

			if (searchTermList.length > 1) {
				filter.operator = 'in';
				filter.ignoreCase = true;
				filter.type = 'display';
			} else {
				filter.operator = 'containsignorecase';
			}

			this._searchTerms.push(filter);
		}

		setLimit(dataRowLimit = this.DEFAULT_ROW_LIMIT) {
			this._dataRowLimit = dataRowLimit;
		}

		setOffset(offset = this.DEFAULT_OFFSET) {
			this._offset = offset;
			return this._offset;
		}

		_toJSON() {
			let dataItems = [];
			let projections = [];
			const querySelectionBuilder = new QuerySelectionBuilder(this._dataItems);
			if (this._dataItems) {
				this._dataItems.forEach(dataItem => {
					const resultItem = {
						id: dataItem.getId(),
						itemId: dataItem.getColumnId()
					};
					const selectionMap = {};
					// Use DataItemAPI to generate the selection
					//todo: second param is filterList
					_.each(querySelectionBuilder.build(dataItem, [], this._searchTerms), (selection) => {
						selectionMap[selection.operation] = selection;
					});

					_.each(dataItem.getCommandList(), command => {
						selectionMap[command.operation] = command;
					});
					const selections = _.values(selectionMap);
					if (selections && selections.length) {
						resultItem.selection = selections;
					}

					const aggregation = dataItem.getAggregation();
					if (!_.isNull(aggregation) && aggregation !== 'none' && (dataItem.getType() === 'fact' || !dataItem.hasDefaultAggregation())) {
						resultItem.aggregate = dataItem.getAggregation();
					}
					dataItems.push(resultItem);
					const projected = dataItem.getProjected();
					if (projected === undefined || projected) {
						projections.push(dataItem.getId());
					}
				});
			}

			let queryFilters = [];
			let filterQuerySpec;
			if (this._filters && this._filters.length) {
				const keptFilters = DataQueryUtils.convertExcludeEmptyValuesFilters(this._filters, this._dataSource);
				filterQuerySpec = new FilterQuerySpec();
				filterQuerySpec.addFiltersToSpec(keptFilters);
			}

			if (this._searchTerms && this._searchTerms.length) {
				filterQuerySpec = filterQuerySpec || new FilterQuerySpec();
				filterQuerySpec.addFiltersToSpec(this._searchTerms);
			}

			if (filterQuerySpec) {
				queryFilters = filterQuerySpec.getFilterSpec();
			}


			let spec = {
				version: this.VERSION,
				dataItems: dataItems,
				projections: projections,
				limit: this._dataRowLimit || this.DEFAULT_ROW_LIMIT,
				filters: queryFilters
			};

			if (this._type) {
				spec.type = this._type;
			}

			if(this._offset){
				spec.offset = this._offset;
			}

			return spec;
		}

		executeQuery(requestOptions = {}) {
			const querySpec = this._toJSON();
			return this._queryService.executeQuery(this._dataSource.getId(), querySpec, this.constructor.name, requestOptions);
		}
	}

	return Query;

});
